/**
 * StakeholderRecommendations Component
 * AI-powered stakeholder suggestions for client portal
 * Features: Generate recommendations, accept/reject/dismiss with one-click
 */
import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Building2, Network, TrendingUp, Zap, Sparkles, Eye } from 'lucide-react';
import { api } from '../api';
import './StakeholderRecommendations.css';

interface Stakeholder {
  id: string;
  name: string;
  profile_image: string | null;
  title: string | null;
  category: string | null;
  organization: string | null;
  affiliation: string | null;
  bio: string | null;
}

interface MatchReason {
  type: string;
  description: string;
  keywords?: string[];
  connections?: string[];
}

interface Recommendation {
  id: string;
  stakeholder: Stakeholder;
  score: number;
  match_reasons: MatchReason[];
  status: string;
  generated_at: string;
}

interface Stats {
  pending: number;
  accepted: number;
  rejected: number;
  dismissed: number;
}

// Get initials from name
const getInitials = (name: string | null): string => {
  if (!name || typeof name !== 'string') return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// Score badge color class
const getScoreColorClass = (score: number): string => {
  if (score >= 70) return 'score-high';
  if (score >= 50) return 'score-medium';
  if (score >= 30) return 'score-low';
  return 'score-minimal';
};

// Match reason icon
const getReasonIcon = (type: string) => {
  switch (type) {
    case 'keyword_match':
      return <FileText className="w-4 h-4 text-blue-500" />;
    case 'sector_alignment':
      return <Building2 className="w-4 h-4 text-green-500" />;
    case 'network_proximity':
      return <Network className="w-4 h-4 text-purple-500" />;
    case 'article_cooccurrence':
      return <TrendingUp className="w-4 h-4 text-orange-500" />;
    case 'power_type':
      return <Zap className="w-4 h-4 text-yellow-500" />;
    default:
      return <Sparkles className="w-4 h-4 text-gray-500" />;
  }
};

// Profile Picture Component
const ProfilePicture: React.FC<{ stakeholder: Stakeholder; size?: string }> = ({ 
  stakeholder, 
  size = 'medium' 
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  const hasValidImage = stakeholder?.profile_image && !imageError;

  return (
    <div className={`profile-picture ${size}`}>
      {hasValidImage ? (
        <>
          <img
            src={stakeholder.profile_image!}
            alt={stakeholder.name || 'Profile'}
            className={imageLoading ? 'loading' : 'loaded'}
            onLoad={() => setImageLoading(false)}
            onError={() => {
              setImageError(true);
              setImageLoading(false);
            }}
          />
          {imageLoading && (
            <div className="initials-fallback">
              {getInitials(stakeholder.name)}
            </div>
          )}
        </>
      ) : (
        <div className="initials-fallback">
          {getInitials(stakeholder.name)}
        </div>
      )}
    </div>
  );
};

// Recommendation Card Component
const RecommendationCard: React.FC<{
  recommendation: Recommendation;
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  onDismiss: (id: string) => void;
  isProcessing: boolean;
}> = ({ recommendation, onAccept, onReject, onDismiss, isProcessing }) => {
  const [expanded, setExpanded] = useState(false);
  const stakeholder = recommendation.stakeholder;
  const matchReasons = recommendation.match_reasons || [];

  return (
    <div className="recommendation-card">
      <div className="card-content">
        <ProfilePicture stakeholder={stakeholder} size="large" />
        
        <div className="card-main">
          <div className="card-header">
            <div className="stakeholder-info">
              <h4 className="stakeholder-name">
                {stakeholder?.name || 'Unknown Stakeholder'}
              </h4>
              <p className="stakeholder-title">
                {stakeholder?.title || 'No title'}
              </p>
              {stakeholder?.organization && stakeholder.organization !== stakeholder.title && (
                <p className="stakeholder-organization">
                  {stakeholder.organization}
                </p>
              )}
              {stakeholder?.affiliation && (
                <p className="stakeholder-affiliation">
                  {stakeholder.affiliation}
                </p>
              )}
            </div>
            
            <div className={`score-badge ${getScoreColorClass(recommendation.score)}`}>
              {recommendation.score}% match
            </div>
          </div>

          <div className="card-badges">
            {stakeholder?.category && (
              <span className="badge category-badge">{stakeholder.category}</span>
            )}
          </div>

          <div className="match-reasons-section">
            <button 
              className="reasons-toggle"
              onClick={() => setExpanded(!expanded)}
            >
              <span className="sparkle-icon">✨</span>
              <span>{matchReasons.length} match reason{matchReasons.length !== 1 ? 's' : ''}</span>
              <span className="chevron">{expanded ? '▲' : '▼'}</span>
            </button>

            {expanded && (
              <div className="reasons-list">
                {matchReasons.map((reason, idx) => (
                  <div key={idx} className="reason-item">
                    <div className="reason-icon">{getReasonIcon(reason.type)}</div>
                    <div className="reason-content">
                      <span className="reason-description">{reason.description}</span>
                      {reason.keywords && reason.keywords.length > 0 && (
                        <div className="reason-keywords">
                          {reason.keywords.map((kw, i) => (
                            <span key={i} className="keyword-tag">{kw}</span>
                          ))}
                        </div>
                      )}
                      {reason.connections && reason.connections.length > 0 && (
                        <div className="reason-connections">
                          via: {reason.connections.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card-actions">
            <button
              className="action-btn accept-btn"
              onClick={() => onAccept(recommendation.id)}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <span className="spinner"></span>
              ) : (
                <>
                  <span>✓</span> Accept
                </>
              )}
            </button>
            <button
              className="action-btn reject-btn"
              onClick={() => onReject(recommendation.id)}
              disabled={isProcessing}
            >
              <span>✕</span> Reject
            </button>
            <button
              className="action-btn dismiss-btn"
              onClick={() => onDismiss(recommendation.id)}
              disabled={isProcessing}
            >
              <Eye size={16} /> Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const StakeholderRecommendations: React.FC = () => {
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [stats, setStats] = useState<Stats>({ pending: 0, accepted: 0, rejected: 0, dismissed: 0 });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch recommendations
  const fetchRecommendations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.getRecommendations();
      
      if (response.success) {
        setRecommendations(response.recommendations || []);
        // Fetch stats separately
        const statsResponse = await api.getRecommendationStats();
        if (statsResponse.success) {
          setStats(statsResponse.stats || { pending: 0, accepted: 0, rejected: 0, dismissed: 0 });
        }
      } else {
        setError(response.error || 'Failed to load recommendations');
      }
    } catch (err) {
      console.error('Error fetching recommendations:', err);
      setError('Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  }, []);

  // Generate new recommendations
  const generateRecommendations = async () => {
    try {
      setGenerating(true);
      setError(null);
      
      const response = await api.generateRecommendations({ limit: 20, minScore: 30 });
      
      if (response.success) {
        setRecommendations(response.recommendations || []);
        setSuccessMessage(`Generated ${response.count} recommendations`);
        setTimeout(() => setSuccessMessage(null), 3000);
        // Fetch updated stats
        const statsResponse = await api.getRecommendationStats();
        if (statsResponse.success) {
          setStats(statsResponse.stats || { pending: 0, accepted: 0, rejected: 0, dismissed: 0 });
        }
      } else {
        setError(response.error || 'Failed to generate recommendations');
      }
    } catch (err) {
      console.error('Error generating recommendations:', err);
      setError('Failed to generate recommendations');
    } finally {
      setGenerating(false);
    }
  };

  // Handle accept
  const handleAccept = async (recommendationId: string) => {
    try {
      setProcessingId(recommendationId);
      
      const response = await api.actOnRecommendation(recommendationId, 'accept');
      
      if (response.success) {
        setRecommendations(prev => prev.filter(r => r.id !== recommendationId));
        setStats(prev => ({ ...prev, pending: prev.pending - 1, accepted: prev.accepted + 1 }));
        setSuccessMessage('Stakeholder added to your network');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(response.error || 'Failed to accept recommendation');
      }
    } catch (err) {
      console.error('Error accepting recommendation:', err);
      setError('Failed to accept recommendation');
    } finally {
      setProcessingId(null);
    }
  };

  // Handle reject
  const handleReject = async (recommendationId: string) => {
    try {
      setProcessingId(recommendationId);
      
      const response = await api.actOnRecommendation(recommendationId, 'reject');
      
      if (response.success) {
        setRecommendations(prev => prev.filter(r => r.id !== recommendationId));
        setStats(prev => ({ ...prev, pending: prev.pending - 1, rejected: prev.rejected + 1 }));
      } else {
        setError(response.error || 'Failed to reject recommendation');
      }
    } catch (err) {
      console.error('Error rejecting recommendation:', err);
      setError('Failed to reject recommendation');
    } finally {
      setProcessingId(null);
    }
  };

  // Handle dismiss
  const handleDismiss = async (recommendationId: string) => {
    try {
      setProcessingId(recommendationId);
      
      const response = await api.actOnRecommendation(recommendationId, 'dismiss');
      
      if (response.success) {
        setRecommendations(prev => prev.filter(r => r.id !== recommendationId));
        setStats(prev => ({ ...prev, pending: prev.pending - 1, dismissed: prev.dismissed + 1 }));
      } else {
        setError(response.error || 'Failed to dismiss recommendation');
      }
    } catch (err) {
      console.error('Error dismissing recommendation:', err);
      setError('Failed to dismiss recommendation');
    } finally {
      setProcessingId(null);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  return (
    <div className="recommendations-container">
      {/* Header */}
      <div className="recommendations-header">
        <div className="header-left">
          <div className="header-icon">✨</div>
          <div className="header-text">
            <h2>Stakeholder Recommendations</h2>
            <p>AI-powered suggestions based on your business priorities and network</p>
          </div>
        </div>
        <button
          className="generate-btn"
          onClick={generateRecommendations}
          disabled={generating}
        >
          {generating ? (
            <>
              <span className="spinner"></span>
              Generating...
            </>
          ) : (
            <>
              <span>🔄</span> Generate New
            </>
          )}
        </button>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card pending">
          <div className="stat-value">{stats.pending}</div>
          <div className="stat-label">Pending</div>
        </div>
        <div className="stat-card accepted">
          <div className="stat-value">{stats.accepted}</div>
          <div className="stat-label">Accepted</div>
        </div>
        <div className="stat-card rejected">
          <div className="stat-value">{stats.rejected}</div>
          <div className="stat-label">Rejected</div>
        </div>
        <div className="stat-card dismissed">
          <div className="stat-value">{stats.dismissed}</div>
          <div className="stat-label">Dismissed</div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="message error-message">{error}</div>
      )}
      
      {successMessage && (
        <div className="message success-message">{successMessage}</div>
      )}

      {/* Recommendations List */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner large"></div>
          <span>Loading recommendations...</span>
        </div>
      ) : recommendations.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">👥</div>
          <h3>No Pending Recommendations</h3>
          <p>Click "Generate New" to get AI-powered stakeholder suggestions based on your profile.</p>
          <button
            className="generate-btn primary"
            onClick={generateRecommendations}
            disabled={generating}
          >
            {generating ? (
              <>
                <span className="spinner"></span>
                Generating...
              </>
            ) : (
              <>
                <span>✨</span> Generate Recommendations
              </>
            )}
          </button>
        </div>
      ) : (
        <div className="recommendations-list">
          {recommendations.map(rec => (
            <RecommendationCard
              key={rec.id}
              recommendation={rec}
              onAccept={handleAccept}
              onReject={handleReject}
              onDismiss={handleDismiss}
              isProcessing={processingId === rec.id}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default StakeholderRecommendations;
